import { Spherical, Vector3 } from 'three';

/**
 * Mixin to handle 3D model rotation from touch events.
 * Uses requestAnimationFrame for smooth 60fps animation.
 * Optimized to reuse Three.js objects and avoid allocations.
 * Requires component to have getOrbitControls(), getCamera(), and render() methods.
 */
export default {
  mounted() {
    // Listen for rotation events from spacer (ConfiguratorEditor)
    // Pre-resolve orbitControls for better performance
    this.getOrbitControls().then(controls => {
      this._cachedOrbitControls = controls;
    });

    // Reusable objects to avoid allocations during animation
    this._reusableSpherical = new Spherical();
    this._reusableVector = new Vector3();

    this._pendingRotation = 0;
    this._rotationFrameId = null;
    this.$root.$on('rotate-3d-model', this.handleRotateModel);
  },
  beforeDestroy() {
    // Remove rotation event listener and cancel pending frame
    this.$root.$off('rotate-3d-model', this.handleRotateModel);
    if (this._rotationFrameId) {
      cancelAnimationFrame(this._rotationFrameId);
    }
  },
  methods: {
    /**
     * Handle rotation from spacer touch (ConfiguratorEditor)
     * Uses requestAnimationFrame for smooth animation
     * @param {number} deltaX - Horizontal movement in pixels
     */
    handleRotateModel(deltaX) {
      // Accumulate rotation delta
      this._pendingRotation += deltaX;

      // Schedule render on next frame if not already scheduled
      if (!this._rotationFrameId) {
        this._rotationFrameId = requestAnimationFrame(() => {
          this._rotationFrameId = null;

          if (!this._cachedOrbitControls || this._pendingRotation === 0) return;

          // Convert pixel delta to rotation angle
          const rotationSpeed = 0.01;
          const angle = this._pendingRotation * rotationSpeed;
          this._pendingRotation = 0;

          // Rotate the camera around the target (reuse objects to avoid allocations)
          const camera = this.getCamera();
          const target = this._cachedOrbitControls.target;

          // Calculate relative position: reusableVector = camera.position - target
          this._reusableVector.subVectors(camera.position, target);

          // Convert to spherical coordinates and rotate
          this._reusableSpherical.setFromVector3(this._reusableVector);
          this._reusableSpherical.theta -= angle;

          // Convert back to cartesian and add target offset
          camera.position.setFromSpherical(this._reusableSpherical).add(target);
          camera.lookAt(target);

          // Render the scene
          this.render(true);
        });
      }
    },
  },
};
